import { Box } from '@chakra-ui/react';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Landings } from 'src/pages/Landings';
import { NavBarLayout } from 'src/components/templates/NavBarLayout';
import {
  CurrentAuthState,
  UserRole,
  useAuthContext,
} from 'src/contexts/AuthContext';
import { AppRoutes } from './routes';
import { DataIntegration } from 'src/pages/DataIntegration';
import { SubscriptionExpired } from 'src/pages/SubscriptionExpired';
import { CustomerSettingsContextProvider } from 'src/contexts/CustomerSettingsContext/CustomerSettingsContextProvider';
import { FilterContextProvider } from 'src/contexts/FilterContext/FilterContextProvider';
import { DataLoading } from 'src/components/molecules/DataStatusWrapper/DataLoading';
import { useAppContext } from 'src/contexts/AppContext/index';

// Yapamar staff Cognito groups — the UserRole enum values ARE the literal
// group names from the JWT ('yapamar_admins' etc.), matching the backend's
// COGNITO_*_GROUP configuration. Staff detection MUST use raw group
// membership, never userProfile.role: getUserProfile defaults role to
// VIEWER for users with no staff group at all, so role === VIEWER does not
// mean staff — checking role would exempt every regular customer from the
// subscription wall.
const STAFF_GROUPS: string[] = [
  UserRole.ADMIN,
  UserRole.ANALYST,
  UserRole.VIEWER,
];

export function App() {
  const { userProfile, currentAuthState, refreshAuthState } = useAuthContext();
  const { currentCustomer, customersList } = useAppContext();

  const isStaff =
    userProfile?.groups?.some((group) => STAFF_GROUPS.includes(group)) ?? false;
  const subscriptionExpired =
    currentCustomer?.subscriptionExpired === true && !isStaff;

  /**
   * Make sure the context is kept in sync -
   * whenever the app is reloaded, refresh
   * the authentication state.
   */
  useEffect(() => {
    refreshAuthState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avoid rendering flicker while auth state initializes
  if (
    currentAuthState === CurrentAuthState.Loading ||
    customersList.isLoading
  ) {
    return (
      <Box w="100vw" h="100vh">
        <DataLoading />
      </Box>
    );
  }

  return currentAuthState === CurrentAuthState.SignedIn &&
    userProfile?.email ? (
    subscriptionExpired ? (
      <BrowserRouter>
        <Routes>
          <Route
            path="/subscription-expired"
            element={<SubscriptionExpired />}
          />
          <Route
            path="*"
            element={<Navigate to="/subscription-expired" replace />}
          />
        </Routes>
      </BrowserRouter>
    ) : currentCustomer?.isRegistrationStarted ? (
      <DataIntegration />
    ) : (
      <div className="App">
        <BrowserRouter>
          <CustomerSettingsContextProvider>
            <FilterContextProvider>
              <NavBarLayout>
                <AppRoutes />
              </NavBarLayout>
            </FilterContextProvider>
          </CustomerSettingsContextProvider>
        </BrowserRouter>
      </div>
    )
  ) : (
    <Landings />
  );
}
